import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Divider,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useScraperForm } from "@hooks/useScraperForm";
import { PageHeader } from "@components/PageHeader";
import {
  FormSectionCard,
  StringListEditor,
  TransformerFieldsRow,
} from "@components/scraper-form";
import {
  CRON_PRESETS,
  CONTENT_FIELD_TYPE_OPTIONS,
  CONTENT_TAKE_MODE_OPTIONS,
  HttpMethod,
  HTTP_METHOD_OPTIONS,
  LINK_EXTRACTOR_TYPE_OPTIONS,
  SubmitConfigType,
} from "@/constants/scraper";

export default function ScraperForm() {
  const f = useScraperForm();

  return (
    <Box>
      <PageHeader>
        {f.isEdit ? f.t("scrapers.editScraper") : f.t("scrapers.newScraper")}
      </PageHeader>

      <FormSectionCard title={f.t("scraperForm.basic")}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={f.t("scraperForm.nameLabel")}
              value={f.name}
              onChange={(e) => f.setName(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={f.t("scraperForm.baseUrl")}
              value={f.base}
              onChange={(e) => f.setBase(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label={f.t("scraperForm.favicon")}
              value={f.favicon}
              onChange={(e) => f.setFavicon(e.target.value)}
            />
          </Grid>
        </Grid>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          {f.t("scraperForm.roots")}
        </Typography>
        <StringListEditor
          values={f.roots}
          onChange={f.setRoots}
          addLabel={f.t("scraperForm.addRoot")}
          retainAtLeastOneRow
        />
      </FormSectionCard>

      <FormSectionCard title={f.t("scraperForm.linkExtraction")}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              select
              label={f.t("scraperForm.method")}
              value={f.linkMethod}
              onChange={(e) => f.setLinkMethod(e.target.value)}
            >
              {HTTP_METHOD_OPTIONS.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              select
              label={f.t("scraperForm.extractionType")}
              value={f.linkType}
              onChange={(e) => f.setLinkType(e.target.value)}
            >
              {LINK_EXTRACTOR_TYPE_OPTIONS.map((lt) => (
                <MenuItem key={lt} value={lt}>
                  {lt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label={f.t("scraperForm.selector")}
              value={f.linkSelector}
              onChange={(e) => f.setLinkSelector(e.target.value)}
            />
          </Grid>
        </Grid>
      </FormSectionCard>

      <FormSectionCard title={f.t("scraperForm.contentRemoval")}>
        <StringListEditor
          values={f.removeSelectors}
          onChange={f.setRemoveSelectors}
          addLabel={f.t("scraperForm.addRemoval")}
          fieldLabel={f.t("scraperForm.selector")}
          retainAtLeastOneRow
        />
      </FormSectionCard>

      <FormSectionCard title={f.t("scraperForm.contentExtractors")}>
        {f.extractors.map((ext, i) => (
          <Box key={i}>
            {i > 0 && <Divider sx={{ my: 2 }} />}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={f.t("scraperForm.property")}
                  value={ext.property}
                  onChange={(e) =>
                    f.patchExtractor(i, { property: e.target.value })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={f.t("scraperForm.selector")}
                  value={ext.selector}
                  onChange={(e) =>
                    f.patchExtractor(i, { selector: e.target.value })
                  }
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label={f.t("scraperForm.extractorType")}
                  value={ext.type}
                  onChange={(e) =>
                    f.patchExtractor(i, { type: e.target.value })
                  }
                >
                  {CONTENT_FIELD_TYPE_OPTIONS.map((ft) => (
                    <MenuItem key={ft} value={ft}>
                      {ft}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label={f.t("scraperForm.take")}
                  value={ext.take}
                  onChange={(e) =>
                    f.patchExtractor(i, { take: e.target.value })
                  }
                >
                  {CONTENT_TAKE_MODE_OPTIONS.map((tk) => (
                    <MenuItem key={tk} value={tk}>
                      {tk}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid
                size={{ xs: 12, sm: 2 }}
                sx={{ display: "flex", alignItems: "center" }}
              >
                {f.extractors.length > 1 && (
                  <IconButton
                    onClick={() =>
                      f.setExtractors(f.extractors.filter((_, j) => j !== i))
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              {f.t("scraperForm.extractorRemove")}
            </Typography>
            <StringListEditor
              values={ext.remove}
              onChange={(next) => f.patchExtractor(i, { remove: next })}
              addLabel={f.t("scraperForm.addExtractorRemove")}
              fieldLabel={f.t("scraperForm.selector")}
            />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {f.t("scraperForm.transformers")}
            </Typography>
            {ext.transformers.map((tr, ti) => (
              <TransformerFieldsRow
                key={ti}
                row={tr}
                onPatch={(patch) => f.patchTransformer(i, ti, patch)}
                onRemove={() =>
                  f.patchExtractor(i, {
                    transformers: ext.transformers.filter((_, j) => j !== ti),
                  })
                }
                onResetType={(nextType) =>
                  f.patchTransformer(i, ti, {
                    ...f.emptyTransformer(),
                    type: nextType,
                  })
                }
              />
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                f.patchExtractor(i, {
                  transformers: [...ext.transformers, f.emptyTransformer()],
                })
              }
            >
              {f.t("scraperForm.addTransformer")}
            </Button>
          </Box>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => f.setExtractors([...f.extractors, f.emptyExtractor()])}
          sx={{ mt: 2 }}
        >
          {f.t("scraperForm.addExtractor")}
        </Button>
      </FormSectionCard>

      <FormSectionCard title={f.t("scraperForm.submitConfig")}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              select
              label={f.t("scraperForm.submitType")}
              value={f.submitType}
              onChange={(e) => f.setSubmitType(e.target.value)}
            >
              <MenuItem value={SubmitConfigType.Store}>
                {SubmitConfigType.Store}
              </MenuItem>
              <MenuItem value={SubmitConfigType.Request}>
                {SubmitConfigType.Request}
              </MenuItem>
            </TextField>
          </Grid>
          {f.submitType === SubmitConfigType.Request && (
            <>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label={f.t("scraperForm.submitUrl")}
                  value={f.submitUrl}
                  onChange={(e) => f.setSubmitUrl(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  select
                  label={f.t("scraperForm.submitMethod")}
                  value={f.submitMethod}
                  onChange={(e) => f.setSubmitMethod(e.target.value)}
                >
                  <MenuItem value={HttpMethod.POST}>{HttpMethod.POST}</MenuItem>
                  <MenuItem value={HttpMethod.PUT}>{HttpMethod.PUT}</MenuItem>
                </TextField>
              </Grid>
            </>
          )}
        </Grid>
      </FormSectionCard>

      {!f.isEdit && (
        <FormSectionCard title={f.t("scraperForm.cronSchedule")}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label={f.t("scraperForm.cronName")}
                value={f.cronName}
                onChange={(e) => f.setCronName(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                select
                label={f.t("scraperForm.cronExpression")}
                value={f.cronExpression}
                onChange={(e) => f.setCronExpression(e.target.value)}
              >
                <MenuItem value="">{f.t("scraperForm.cronNone")}</MenuItem>
                {CRON_PRESETS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {f.t(`scraperForm.cronPreset.${p.labelKey}`)} ({p.value})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </FormSectionCard>
      )}

      <Box sx={{ display: "flex", gap: 2 }}>
        <Button variant="contained" onClick={f.handleSubmit}>
          {f.t("scraperForm.save")}
        </Button>
        <Button variant="outlined" onClick={() => f.navigate("/scrapers")}>
          {f.t("scraperForm.cancel")}
        </Button>
      </Box>
    </Box>
  );
}
